import React, { useEffect, useState } from 'react';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import { Grid, Typography, Card, Box, makeStyles, Button, IconButton } from '@material-ui/core';
import {
  RenderTextField,
  RenderSelectField,
  RenderAutocompleteField,
  Utils,
  RenderRecaptcha,
} from '@mvrb-frontend/shared-ui';
import Dropzone from 'react-dropzone';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DescriptionIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  dropzone: {
    border: `3px dashed ${theme.palette.primary.main}`,
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  dropBackground: {
    backgroundColor: '#F6F6F9',
    padding: '20px',
    borderRadius: '4px',
  },
  recaptcha: {
    marginTop: '20px',
  },
  icon: {
    fontSize: '4.5rem',
  },
  fileIcon: {
    color: '#E67337',
  },
  card: {
    marginTop: '20px',
    padding: '30px',
  },
  infoText: {
    fontSize: '0.75rem',
  },
  fileText: {
    color: theme.palette.primary.main,
    textOverflow: 'ellipsis',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '100%',
    padding: 0,
    margin: '5px',
  },
  fileSize: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    padding: 0,
    whiteSpace: 'nowrap',
    margin: '5px',
  },
  fileButton: {
    width: '100%',
  },
  fileError: {
    color: '#F44336',
    marginLeft: '8px',
  },
  bold: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

const FileUploadForm = props => {
  const classes = useStyles();
  const { boardItems, staffMembers, setAttachment, attachment, reset } = props;

  const [attachmentSize, setAttachmentSize] = useState<any>(0);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [fileName, setFileName] = useState('');

  const handleDrop = (acceptedFiles: File[]) => {
    setFieldValue('fileSize', acceptedFiles[0].size);
    setFieldTouched('fileSize');
    setFileName('');

    if (acceptedFiles[0].size <= parseInt(process.env.MAX_FTP_UPLOAD_FILE_SIZE || '5000') * 1024 * 1024) {
      setFieldValue('file', true);
      setAttachmentSize(acceptedFiles[0].size);
      setAttachment(acceptedFiles[0]);
    } else {
      setFileName(acceptedFiles[0].name);
      setAttachmentSize(0);
      setAttachment(null);
      setFieldValue('file', false);
    }
  };

  useEffect(() => {
    if (reset) {
      setFileName('');
    }
  }, [reset]);

  const removeFile = () => {
    setFieldValue('file', false);
    setFieldValue('fileSize', undefined);
    setAttachment(null);
  };

  return (
    <Card className={classes.card}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Field
                name="boardId"
                label="Board (Required)"
                placeholder="Required"
                component={RenderSelectField}
                options={boardItems}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={'staffIds'}
                label={'Select Staff Contacts (Select one or more)'}
                placeholder={'Required'}
                component={RenderAutocompleteField}
                options={staffMembers}
                reset={reset}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={'email'}
                label={'Email Address (Required)'}
                placeholder={'Required'}
                component={RenderTextField}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={'projectName'}
                className="w-full"
                label={'Project (Required)'}
                placeholder={'Required'}
                component={RenderTextField}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={'fileNumber'}
                className="w-full"
                label={'File Number (Optional)'}
                component={RenderTextField}
              />
              <Typography variant="body1" className={classes.infoText}>
                (If the document you’re uploading is a requirement of a Land Use Permit or Water License, please include
                the relevant file #)
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Field
                name={'description'}
                className="w-full"
                label={'Document Description (Required)'}
                component={RenderTextField}
                characterLimit={1000}
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={'additionalStaffNotes'}
                className="w-full"
                label={'Additional Notes for Staff (Optional)'}
                component={RenderTextField}
                max={1000}
                characterLimit={1000}
                rows={3}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Dropzone
            onDrop={file => handleDrop(file)}
            multiple={false}
            maxSize={parseInt(process.env.MAX_FTP_UPLOAD_FILE_SIZE || '5000') * 1024 * 1024}
          >
            {({ getRootProps, getInputProps }) => (
              <Box className={classes.dropBackground}>
                <Box p={2} className={classes.dropzone} {...getRootProps()}>
                  <input {...getInputProps()} multiple={false} />
                  <Box>
                    <CloudUploadIcon color="primary" className={classes.icon} />
                  </Box>
                  <Box>
                    <Typography>
                      Drag your file here, or <span className={classes.bold}>browse</span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Dropzone>
          <Box className={classes.fileError}>
            {fileName && (
              <Box>
                <Grid container alignItems="center">
                  <Grid item>
                    <Typography color="error">{fileName}</Typography>
                  </Grid>
                  <Grid>
                    <IconButton>
                      <DeleteIcon onClick={() => setFileName('')} />
                    </IconButton>
                  </Grid>
                </Grid>
                <ErrorMessage name="fileSize" />
              </Box>
            )}
            <Box>
              <ErrorMessage name="file" />
            </Box>
          </Box>
          {attachment && (
            <Box my={1}>
              <Button
                className={classes.fileButton}
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<DescriptionIcon className={classes.fileIcon} />}
                endIcon={<DeleteIcon />}
                onClick={removeFile}
              >
                <Typography className={classes.fileText} variant="body1">
                  {`${attachment.path}`}
                </Typography>
                <Typography className={classes.fileSize} variant="body1">
                  {`(${Utils.getFileSize(attachmentSize)})`}
                </Typography>
              </Button>
            </Box>
          )}

          <Grid item xs={12}>
            <Box className={classes.recaptcha}>
              <Field name={'recaptcha'} className="w-full" component={RenderRecaptcha} reset={reset} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

FileUploadForm.propTypes = {
  boardItems: PropTypes.array.isRequired,
  staffMembers: PropTypes.array.isRequired,
  attachment: PropTypes.object,
  setAttachment: PropTypes.func.isRequired,
  reset: PropTypes.bool,
};

export default FileUploadForm;
